import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ChartHeader = ({ 
  selectedSymbol, 
  onSymbolChange, 
  selectedTimeframe, 
  onTimeframeChange,
  onTemplateLoad,
  onTemplateSave 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSymbolSearch, setShowSymbolSearch] = useState(false);
  const [favoriteSymbols, setFavoriteSymbols] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);

  // Mock symbols data
  const allSymbols = [
    { symbol: 'EURUSD', name: 'Euro / US Dollar', category: 'Major' },
    { symbol: 'GBPUSD', name: 'British Pound / US Dollar', category: 'Major' },
    { symbol: 'USDJPY', name: 'US Dollar / Japanese Yen', category: 'Major' },
    { symbol: 'USDCHF', name: 'US Dollar / Swiss Franc', category: 'Major' },
    { symbol: 'AUDUSD', name: 'Australian Dollar / US Dollar', category: 'Major' },
    { symbol: 'USDCAD', name: 'US Dollar / Canadian Dollar', category: 'Major' },
    { symbol: 'NZDUSD', name: 'New Zealand Dollar / US Dollar', category: 'Major' },
    { symbol: 'EURJPY', name: 'Euro / Japanese Yen', category: 'Cross' },
    { symbol: 'GBPJPY', name: 'British Pound / Japanese Yen', category: 'Cross' },
    { symbol: 'EURGBP', name: 'Euro / British Pound', category: 'Cross' },
    { symbol: 'XAUUSD', name: 'Gold / US Dollar', category: 'Metals' },
    { symbol: 'XAGUSD', name: 'Silver / US Dollar', category: 'Metals' },
    { symbol: 'BTCUSD', name: 'Bitcoin / US Dollar', category: 'Crypto' },
    { symbol: 'ETHUSD', name: 'Ethereum / US Dollar', category: 'Crypto' }
  ];

  const timeframes = [
    { value: '1m', label: '1 Minute' },
    { value: '5m', label: '5 Minutes' },
    { value: '15m', label: '15 Minutes' },
    { value: '30m', label: '30 Minutes' },
    { value: '1h', label: '1 Hour' },
    { value: '4h', label: '4 Hours' },
    { value: '1d', label: '1 Day' },
    { value: '1w', label: '1 Week' },
    { value: '1M', label: '1 Month' }
  ];

  // Initialize favorites and templates
  useEffect(() => {
    const savedFavorites = localStorage.getItem('trading-favorites');
    if (savedFavorites) {
      setFavoriteSymbols(JSON.parse(savedFavorites));
    } else {
      const defaultFavorites = ['EURUSD', 'GBPUSD', 'USDJPY', 'XAUUSD'];
      setFavoriteSymbols(defaultFavorites);
      localStorage.setItem('trading-favorites', JSON.stringify(defaultFavorites));
    }

    // Mock templates
    const mockTemplates = [
      { id: 1, name: 'Scalping Setup', description: 'Short-term trading indicators' },
      { id: 2, name: 'Swing Trading', description: 'Medium-term trend analysis' },
      { id: 3, name: 'Trend Following', description: 'Long-term trend indicators' },
      { id: 4, name: 'Support & Resistance', description: 'Key level analysis' }
    ];
    setTemplates(mockTemplates);
  }, []);

  const filteredSymbols = allSymbols?.filter(symbol =>
    symbol?.symbol?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    symbol?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const groupedSymbols = filteredSymbols?.reduce((groups, symbol) => {
    const category = symbol?.category;
    if (!groups?.[category]) {
      groups[category] = [];
    }
    groups?.[category]?.push(symbol);
    return groups;
  }, {});

  const toggleFavorite = (symbol) => {
    const newFavorites = favoriteSymbols?.includes(symbol)
      ? favoriteSymbols?.filter(fav => fav !== symbol)
      : [...favoriteSymbols, symbol];
    
    setFavoriteSymbols(newFavorites);
    localStorage.setItem('trading-favorites', JSON.stringify(newFavorites));
  };

  const handleSymbolSelect = (symbol) => {
    onSymbolChange(symbol);
    setShowSymbolSearch(false);
    setSearchQuery('');
  };

  const handleTemplateLoad = (template) => {
    onTemplateLoad(template);
    setShowTemplateMenu(false);
  };

  const quickTimeframes = ['1m', '5m', '15m', '1h', '4h', '1d'];

  return (
    <div className="bg-card border-b border-border p-4">
      <div className="flex items-center justify-between">
        {/* Left Section - Symbol Search & Favorites */}
        <div className="flex items-center space-x-4">
          {/* Symbol Selector */}
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowSymbolSearch(!showSymbolSearch)}
              className="min-w-32 justify-between"
            >
              <span className="font-mono font-semibold">{selectedSymbol}</span>
              <Icon name="ChevronDown" size={16} />
            </Button>

            {showSymbolSearch && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                <div className="p-3 border-b border-border">
                  <Input
                    type="search"
                    placeholder="Search symbols..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full"
                  />
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {/* Favorites Section */}
                  {favoriteSymbols?.length > 0 && (
                    <div className="p-2 border-b border-border">
                      <h4 className="text-xs font-medium text-muted-foreground mb-2 px-2">FAVORITES</h4>
                      {favoriteSymbols?.map((symbol) => {
                        const symbolData = allSymbols?.find(s => s?.symbol === symbol);
                        if (!symbolData) return null;
                        
                        return (
                          <button
                            key={symbol}
                            onClick={() => handleSymbolSelect(symbol)}
                            className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center space-x-2">
                              <span className="font-mono font-medium text-sm">{symbol}</span>
                              <span className="text-xs text-muted-foreground">{symbolData?.name}</span>
                            </div>
                            <Icon name="Star" size={14} className="text-amber-500 fill-current" />
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* All Symbols by Category */}
                  {Object.entries(groupedSymbols)?.map(([category, symbols]) => (
                    <div key={category} className="p-2">
                      <h4 className="text-xs font-medium text-muted-foreground mb-2 px-2">{category?.toUpperCase()}</h4>
                      {symbols?.map((symbol) => (
                        <button
                          key={symbol?.symbol}
                          onClick={() => handleSymbolSelect(symbol?.symbol)}
                          className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors"
                        >
                          <div className="flex items-center space-x-2">
                            <span className="font-mono font-medium text-sm">{symbol?.symbol}</span>
                            <span className="text-xs text-muted-foreground">{symbol?.name}</span>
                          </div>
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              toggleFavorite(symbol?.symbol);
                            }}
                            className="p-1 hover:bg-muted-foreground/10 rounded"
                          >
                            <Icon 
                              name="Star" 
                              size={14} 
                              className={favoriteSymbols?.includes(symbol?.symbol) 
                                ? "text-amber-500 fill-current" :"text-muted-foreground"
                              } 
                            />
                          </button>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Timeframe Buttons */}
          <div className="flex items-center space-x-1">
            {quickTimeframes?.map((tf) => (
              <Button
                key={tf}
                variant={selectedTimeframe === tf ? "default" : "ghost"}
                size="sm"
                onClick={() => onTimeframeChange(tf)}
                className="min-w-12"
              >
                {tf}
              </Button>
            ))}
          </div>

          {/* Timeframe Selector */}
          <Select
            options={timeframes}
            value={selectedTimeframe}
            onChange={onTimeframeChange}
            className="w-32"
          />
        </div>

        {/* Right Section - Templates & Actions */}
        <div className="flex items-center space-x-2">
          {/* Template Management */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTemplateMenu(!showTemplateMenu)}
              iconName="Layout"
              iconPosition="left"
            >
              Templates
            </Button>

            {showTemplateMenu && (
              <div className="absolute top-full right-0 mt-1 w-64 bg-popover border border-border rounded-md shadow-lg z-dropdown animate-fade-in">
                <div className="p-2">
                  <div className="mb-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onTemplateSave()}
                      iconName="Save"
                      iconPosition="left"
                      className="w-full justify-start"
                    >
                      Save Current Template
                    </Button>
                  </div>
                  
                  <div className="border-t border-border pt-2">
                    <h4 className="text-xs font-medium text-muted-foreground mb-2 px-2">SAVED TEMPLATES</h4>
                    {templates?.map((template) => (
                      <button
                        key={template?.id}
                        onClick={() => handleTemplateLoad(template)}
                        className="w-full text-left p-2 rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="font-medium text-sm">{template?.name}</div>
                        <div className="text-xs text-muted-foreground">{template?.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Chart Actions */}
          <Button
            variant="outline"
            size="sm"
            iconName="Maximize2"
            title="Fullscreen"
          />
          
          <Button
            variant="outline"
            size="sm"
            iconName="Settings"
            title="Chart Settings"
          />
        </div>
      </div>
      {/* Click outside handlers */}
      {(showSymbolSearch || showTemplateMenu) && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => {
            setShowSymbolSearch(false);
            setShowTemplateMenu(false);
          }}
        />
      )}
    </div>
  );
};

export default ChartHeader;